/**
 * S0_STRICT_BACKEND_MATCH — Production Guard
 * INTENTION: Prevent wrong-host → wrong-backend connections to stop data corruption + auth loops.
 *
 * Rules:
 * - Same-origin "/api" → always OK (no domain baked = no drift possible)
 * - localhost/127.0.0.1 → always OK (dev)
 * - Absolute URL → check against allowMap; mismatch = blocking screen
 * - /api/health validation → check envName matches expected
 */

const ALLOW_MAP = {
  // Production: same-origin /api is primary (guard auto-passes for "/api").
  // These entries are defense-in-depth IF someone re-introduces absolute URLs.
  "sitepe.app": ["sitepe.app", "api.sitepe.app"],
  "www.sitepe.app": ["www.sitepe.app", "sitepe.app", "api.sitepe.app"],
  // Preview environments (Emergent platform)
  // Dynamic preview domains use same-origin /api, so guard auto-passes.
};

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );
}

function showBlockScreen({ host, apiBaseUrl, apiHost, expected, reason }) {
  const diag = {
    host,
    apiBaseUrl: apiBaseUrl || '',
    apiHost: apiHost || '',
    expected: expected || '',
    reason: reason || '',
    ts: new Date().toISOString(),
    userAgent: navigator.userAgent,
  };

  document.documentElement.innerHTML = `
    <head><title>Configuration Error — SitePe ERP</title></head>
    <body style="font-family:system-ui,sans-serif;padding:32px;max-width:600px;margin:0 auto;">
      <div style="background:#fef2f2;border:2px solid #dc2626;border-radius:12px;padding:24px;">
        <h2 style="color:#991b1b;margin:0 0 8px;" data-testid="backend-mismatch-title">
          App Configuration Mismatch
        </h2>
        <p style="color:#7f1d1d;margin:0 0 16px;">
          Wrong build deployed or wrong env config. This build is pointing to the wrong backend.
        </p>
        <table style="width:100%;border-collapse:collapse;font-size:14px;">
          <tr><td style="padding:4px 8px;font-weight:600;">Host</td><td style="padding:4px 8px;">${escapeHtml(host)}</td></tr>
          <tr><td style="padding:4px 8px;font-weight:600;">API Base</td><td style="padding:4px 8px;">${escapeHtml(apiBaseUrl || '')}</td></tr>
          <tr><td style="padding:4px 8px;font-weight:600;">API Host</td><td style="padding:4px 8px;">${escapeHtml(apiHost || '')}</td></tr>
          <tr><td style="padding:4px 8px;font-weight:600;">Expected</td><td style="padding:4px 8px;">${escapeHtml(expected || '')}</td></tr>
          <tr><td style="padding:4px 8px;font-weight:600;">Reason</td><td style="padding:4px 8px;">${escapeHtml(reason || '')}</td></tr>
        </table>
        <div style="margin-top:16px;display:flex;gap:8px;">
          <button data-testid="copy-diagnostics-btn" style="background:#dc2626;color:white;border:none;border-radius:8px;padding:8px 16px;cursor:pointer;font-weight:600;">
            Copy Diagnostics
          </button>
          <button onclick="window.location.reload()" style="background:#6b7280;color:white;border:none;border-radius:8px;padding:8px 16px;cursor:pointer;font-weight:600;">
            Retry
          </button>
        </div>
        <pre style="background:#1f2937;color:#f9fafb;padding:12px;border-radius:8px;margin-top:12px;overflow:auto;font-size:12px;">${escapeHtml(JSON.stringify(diag, null, 2))}</pre>
      </div>
      <script>
        document.querySelector('[data-testid="copy-diagnostics-btn"]').onclick = async () => {
          try {
            await navigator.clipboard.writeText(${JSON.stringify(JSON.stringify(diag))});
            alert('Diagnostics copied to clipboard');
          } catch(e) { alert('Copy failed — manually select the text below'); }
        };
      </script>
    </body>
  `;
}

export function enforceStrictBackendMatch(enabled) {
  if (!enabled) return { ok: true };

  const host = window.location.host;

  // Allow localhost/dev — always safe
  if (host.includes('localhost') || host.startsWith('127.0.0.1')) {
    return { ok: true };
  }

  // Same-origin "/api" — no domain baked, no drift possible
  // Check: no absolute backend URL should exist in the runtime config
  const runtimeUrl = window.__RUNTIME_CONFIG__?.REACT_APP_BACKEND_URL || '';
  if (!runtimeUrl || runtimeUrl === '' || runtimeUrl.startsWith('/')) {
    return { ok: true };
  }

  // Absolute URL detected — validate against allowMap
  let apiHost = '';
  try {
    apiHost = new URL(runtimeUrl).host;
  } catch (e) {
    showBlockScreen({
      host,
      apiBaseUrl: runtimeUrl,
      expected: '(valid URL required)',
      reason: 'Invalid API base URL in runtime config',
    });
    return { ok: false, blocked: true };
  }

  // Check allowMap
  const allowedBackends = ALLOW_MAP[host] || [];

  // If no mapping exists for this host and API is cross-origin, block
  if (allowedBackends.length === 0 && apiHost !== host) {
    showBlockScreen({
      host,
      apiBaseUrl: runtimeUrl,
      apiHost,
      expected: `same-origin or mapped in allowMap`,
      reason: `No allowMap entry for host "${host}" and API is cross-origin`,
    });
    return { ok: false, blocked: true };
  }

  if (allowedBackends.length > 0 && !allowedBackends.includes(apiHost)) {
    showBlockScreen({
      host,
      apiBaseUrl: runtimeUrl,
      apiHost,
      expected: allowedBackends.join(', '),
      reason: 'API host not in allowMap for this frontend host',
    });
    return { ok: false, blocked: true };
  }

  return { ok: true };
}
